import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Flame, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { TICKETS } from "@/data/giveaway";
import { API } from "@/hooks/useGiveawayStats";

const accentText = {
  gold: "text-gold border-gold",
  ember: "text-ember border-ember",
  blood: "text-blood border-blood",
  solana: "text-solana border-solana",
};

export const EntryForm = ({ selectedTier, setSelectedTier, onSuccess }) => {
  const [form, setForm] = useState({ email: "", wallet_address: "", tx_hash: "", currency: "SOL" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.wallet_address || !form.tx_hash) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/giveaway/entries`, { ...form, tier: selectedTier });
      const tier = TICKETS.find((t) => t.tier === selectedTier);
      if (data.status === "verified") {
        toast.success("Verified — you're in the draw! 🔥", {
          description: `Payment confirmed on-chain. ${tier.entries} ${tier.entries > 1 ? "entries" : "entry"} locked in. Good luck, degen.`,
        });
      } else {
        toast.success("Payment received — pending verification ⏳", {
          description: "We couldn't auto-verify on-chain just yet. Your entry is saved and will be confirmed shortly.",
        });
      }
      setDone(true);
      setForm({ email: "", wallet_address: "", tx_hash: "", currency: "SOL" });
      onSuccess?.();
      setTimeout(() => setDone(false), 5000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Verification failed. Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-black/50 border border-white/10 rounded-md px-4 py-3 font-body text-sm text-white placeholder:text-gray-500 focus:border-gold/60 focus:outline-none transition-colors duration-300";

  return (
    <form onSubmit={handleSubmit} data-testid="entry-form" className="glass rounded-lg p-6 md:p-8">
      <label className="block text-xs font-body font-bold uppercase tracking-widest text-gray-400 mb-3">
        Choose your ticket
      </label>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
        {TICKETS.map((t) => (
          <button
            type="button"
            key={t.tier}
            onClick={() => setSelectedTier(t.tier)}
            data-testid={`form-tier-${t.tier}`}
            className={`rounded-md border px-3 py-3 text-left transition-colors duration-300 ${
              selectedTier === t.tier
                ? `bg-white/5 ${accentText[t.accent]}`
                : "border-white/10 text-gray-400 hover:border-white/30"
            }`}
          >
            <span className="block font-display text-lg leading-none">${t.price}</span>
            <span className="block text-[10px] font-body uppercase tracking-wide mt-1">
              {t.name} · {t.entries}x
            </span>
          </button>
        ))}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-body font-bold uppercase tracking-widest text-gray-400 mb-2">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@email.com"
            className={inputClass}
            data-testid="form-email"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-body font-bold uppercase tracking-widest text-gray-400 mb-2">Your Solana Wallet</label>
          <input
            type="text"
            value={form.wallet_address}
            onChange={(e) => update("wallet_address", e.target.value)}
            placeholder="Wallet you sent from (for prize delivery)"
            className={inputClass}
            data-testid="form-wallet"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-body font-bold uppercase tracking-widest text-gray-400 mb-2">Transaction Hash</label>
          <input
            type="text"
            value={form.tx_hash}
            onChange={(e) => update("tx_hash", e.target.value)}
            placeholder="Paste the TX signature of your payment"
            className={inputClass}
            data-testid="form-txhash"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-body font-bold uppercase tracking-widest text-gray-400 mb-2">Paid With</label>
          <div className="flex gap-2">
            {["SOL", "USDC"].map((c) => (
              <button
                type="button"
                key={c}
                onClick={() => update("currency", c)}
                data-testid={`form-currency-${c}`}
                className={`flex-1 rounded-md border px-4 py-3 font-body font-bold text-sm uppercase transition-colors duration-300 ${
                  form.currency === c
                    ? "border-solana text-solana bg-solana/10"
                    : "border-white/10 text-gray-400 hover:border-white/30"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <motion.button
        type="submit"
        disabled={submitting}
        whileTap={{ scale: 0.98 }}
        data-testid="form-submit"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover disabled:opacity-60 text-black font-body font-extrabold text-base px-8 py-4 rounded-md transition-colors duration-300 glow-gold uppercase tracking-wide"
      >
        {submitting ? (
          <><Loader2 className="w-5 h-5 animate-spin" /> Registering...</>
        ) : done ? (
          <><CheckCircle2 className="w-5 h-5" /> Entry Locked In</>
        ) : (
          <><Flame className="w-5 h-5" /> Register My Entry</>
        )}
      </motion.button>
      <p className="mt-3 text-center font-body text-xs text-gray-500">
        Entries are verified against the blockchain before the draw. Duplicate TX hashes are rejected.
      </p>
    </form>
  );
};
